/**
 * @file
 * DOM rendering functions for the layout plan component.
 *
 * @description
 * Builds and updates the SVG canvas, sidebar shape list, detail panel,
 * and breadcrumb navigation. Uses vanilla DOM APIs instead of React JSX.
 */

import type { NormalizedPolygon, NormalizedRootLayer, Point } from './_types';
import type { LayoutPlanStateManager } from './_state';

const SVG_NS = 'http://www.w3.org/2000/svg';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toPointsAttr(points: Point[]): string {
  return points.map((p) => `${p.x},${p.y}`).join(' ');
}

function clearElement(el: HTMLElement | SVGElement): void {
  while (el.firstChild) {
    el.removeChild(el.firstChild);
  }
}

/**
 * Toggle a BEM modifier CSS class on elements matching a data-polygon-id
 * inside a given container.
 */
export function togglePolygonModifier(
  container: HTMLElement,
  oldId: string | null,
  newId: string | null,
  modifier: string
): void {
  if (oldId) {
    const els = container.querySelectorAll(`[data-polygon-id="${oldId}"]`);
    els.forEach((el) => el.classList.remove(modifier));
  }
  if (newId) {
    const els = container.querySelectorAll(`[data-polygon-id="${newId}"]`);
    els.forEach((el) => el.classList.add(modifier));
  }
}

// ---------------------------------------------------------------------------
// Canvas (SVG + background image)
// ---------------------------------------------------------------------------

export function renderCanvas(container: HTMLElement, state: LayoutPlanStateManager, onReady?: () => void): void {
  clearElement(container);

  if (state.backgroundUrl) {
    // Reserve space with a loading state to prevent layout shift.
    container.classList.add('layout-plan__canvas--loading');

    // Create image off-screen so the DOM doesn't shift when it loads.
    const img = new Image();
    img.src = state.backgroundUrl;
    img.alt = '';
    img.setAttribute('aria-hidden', 'true');
    img.className = 'layout-plan__image';

    function reveal(): void {
      container.classList.remove('layout-plan__canvas--loading');
      container.appendChild(img);
      const svg = buildSvgOverlay(img.naturalWidth, img.naturalHeight, state);
      container.appendChild(svg);
      onReady?.();
    }

    // If already cached, reveal immediately; otherwise wait for load.
    if (img.complete && img.naturalWidth > 0) {
      reveal();
    } else {
      img.addEventListener('load', reveal);
    }
  }
}

function buildSvgOverlay(width: number, height: number, state: LayoutPlanStateManager): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.classList.add('layout-plan__svg');
  svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
  svg.setAttribute('role', 'group');
  svg.setAttribute('aria-label', Drupal.t('Interactive layout plan shapes'));

  state.polygons.forEach((polygon) => {
    svg.appendChild(buildPolygonGroup(polygon, state));
  });

  return svg;
}

function buildPolygonGroup(polygon: NormalizedPolygon, state: LayoutPlanStateManager): SVGGElement {
  const isInactive = !polygon.isActive;
  const isSelected = state.selectedId === polygon.id;
  const isHover = state.hoverId === polygon.id;

  const g = document.createElementNS(SVG_NS, 'g');
  g.setAttribute('role', 'group');
  g.setAttribute('aria-label', polygon.name);
  g.setAttribute('data-polygon-id', polygon.id);

  // State class
  const stateClass = isInactive
    ? 'layout-plan__polygon--inactive'
    : isSelected
      ? 'layout-plan__polygon--selected'
      : isHover
        ? 'layout-plan__polygon--hover'
        : '';

  // Polygon element
  const poly = document.createElementNS(SVG_NS, 'polygon');
  poly.classList.add('layout-plan__polygon');
  if (stateClass) poly.classList.add(stateClass);
  poly.setAttribute('data-polygon-id', polygon.id);
  poly.setAttribute('points', toPointsAttr(polygon.points));
  poly.setAttribute('aria-label', `${polygon.name}${isInactive ? ` (${Drupal.t('unavailable')})` : ''}`);
  poly.setAttribute('role', 'button');
  poly.setAttribute('aria-pressed', String(isSelected));
  if (isInactive) poly.setAttribute('aria-disabled', 'true');
  poly.setAttribute('tabindex', isInactive ? '-1' : '0');
  poly.setAttribute('aria-controls', 'layout-plan-panel');

  if (!isInactive) {
    poly.addEventListener('click', () => state.handleShapeClick(polygon.id));
    poly.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        state.handleShapeClick(polygon.id);
      }
    });
    poly.addEventListener('mouseenter', () => state.hover(polygon.id));
    poly.addEventListener('mouseleave', () => state.hover(null));
    poly.addEventListener('focus', () => state.hover(polygon.id));
    poly.addEventListener('blur', () => state.hover(null));
  }

  g.appendChild(poly);

  // Label bubble (at labelCoordinate if available, otherwise center)
  if (polygon.labelCoordinate || polygon.center) {
    const bubble = buildLabelBubble(polygon, isSelected, isHover, isInactive);

    if (!isInactive) {
      bubble.addEventListener('click', () => state.handleShapeClick(polygon.id));
      bubble.addEventListener('mouseenter', () => state.hover(polygon.id));
      bubble.addEventListener('mouseleave', () => state.hover(null));
    }

    g.appendChild(bubble);
  }

  return g;
}

function buildLabelBubble(
  polygon: NormalizedPolygon,
  isSelected: boolean,
  isHover: boolean,
  isInactive: boolean
): SVGGElement {
  const center = polygon.center;
  const labelPos = polygon.labelCoordinate ?? center;
  if (!labelPos) return document.createElementNS(SVG_NS, 'g');

  const label = polygon.propertyCode;
  const fontSize = 22;
  const charWidth = fontSize * 0.55;
  const paddingX = 30;
  const paddingY = 20;
  const rectWidth = Math.max(label.length * charWidth + paddingX * 2, 40);
  const rectHeight = fontSize + paddingY * 2;

  const stateClass = isInactive
    ? 'layout-plan__label-bubble--inactive'
    : isSelected
      ? 'layout-plan__label-bubble--selected'
      : isHover
        ? 'layout-plan__label-bubble--hover'
        : '';

  const outer = document.createElementNS(SVG_NS, 'g');
  outer.classList.add('layout-plan__label-bubble');
  if (stateClass) outer.classList.add(stateClass);

  // When labelCoordinate differs from center, draw a connector line and dot.
  if (center && polygon.labelCoordinate) {
    // Connector line from center to label position
    const line = document.createElementNS(SVG_NS, 'line');
    line.classList.add('layout-plan__label-connector');
    line.setAttribute('x1', String(center.x));
    line.setAttribute('y1', String(center.y));
    line.setAttribute('x2', String(labelPos.x));
    line.setAttribute('y2', String(labelPos.y));
    outer.appendChild(line);

    // Small filled circle at center_coordinate
    const dot = document.createElementNS(SVG_NS, 'circle');
    dot.classList.add('layout-plan__label-dot');
    dot.setAttribute('cx', String(center.x));
    dot.setAttribute('cy', String(center.y));
    dot.setAttribute('r', '10');
    outer.appendChild(dot);
  }

  // Label bubble centered on labelCoordinate (or center fallback)
  const bubbleGroup = document.createElementNS(SVG_NS, 'g');
  bubbleGroup.setAttribute('transform', `translate(${labelPos.x},${labelPos.y})`);

  const wrapper = document.createElementNS(SVG_NS, 'g');
  wrapper.classList.add('layout-plan__label-wrapper');
  wrapper.setAttribute('transform', `translate(${-rectWidth / 2},${-rectHeight / 2})`);

  const rect = document.createElementNS(SVG_NS, 'rect');
  rect.classList.add('layout-plan__label-bg');
  rect.setAttribute('width', String(rectWidth));
  rect.setAttribute('height', String(rectHeight));
  rect.setAttribute('rx', '8');

  const text = document.createElementNS(SVG_NS, 'text');
  text.classList.add('layout-plan__label-text');
  text.setAttribute('x', String(rectWidth / 2));
  text.setAttribute('y', String(rectHeight / 2 + fontSize * 0.36));
  text.setAttribute('font-size', String(fontSize));
  text.setAttribute('text-anchor', 'middle');
  text.textContent = label;

  wrapper.appendChild(rect);
  wrapper.appendChild(text);
  bubbleGroup.appendChild(wrapper);
  outer.appendChild(bubbleGroup);

  return outer;
}

// ---------------------------------------------------------------------------
// Shape list (sidebar)
// ---------------------------------------------------------------------------

/**
 * Render skeleton placeholder buttons matching the number of polygons.
 * Shown while the canvas image is loading.
 */
export function renderShapeListSkeletons(container: HTMLElement, state: LayoutPlanStateManager): void {
  clearElement(container);

  state.polygons.forEach(() => {
    const skeleton = document.createElement('div');
    skeleton.className = 'layout-plan__shape-btn layout-plan__shape-btn--skeleton';
    skeleton.setAttribute('aria-hidden', 'true');
    container.appendChild(skeleton);
  });
}

export function renderShapeList(container: HTMLElement, state: LayoutPlanStateManager): void {
  clearElement(container);

  // Shape buttons
  state.polygons.forEach((polygon) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.setAttribute('data-polygon-id', polygon.id);
    btn.setAttribute('aria-controls', 'layout-plan-panel');

    const isHover = state.hoverId === polygon.id;

    const classes = ['layout-plan__shape-btn'];
    if (isHover) classes.push('layout-plan__shape-btn--hover');
    if (!polygon.isActive) classes.push('layout-plan__shape-btn--inactive');
    btn.className = classes.join(' ');

    btn.disabled = !polygon.isActive;

    // Name only
    const nameSpan = document.createElement('span');
    nameSpan.className = 'layout-plan__shape-btn-name';
    nameSpan.textContent = polygon.name;
    btn.appendChild(nameSpan);

    // Status badge for properties
    if (polygon.property) {
      const badge = document.createElement('span');
      const badgeClasses = ['layout-plan__shape-btn-status'];
      if (!polygon.property.isSold) badgeClasses.push('layout-plan__shape-btn-status--available');
      badge.className = badgeClasses.join(' ');
      badge.textContent = polygon.property.isSold ? Drupal.t('Sold') : Drupal.t('Available');
      btn.appendChild(badge);
    }

    // Drill-down indicator (matches tp:icon pattern)
    if (polygon.childLayers?.length) {
      const arrow = document.createElement('i');
      arrow.className = 'ph ph-arrow-right icon icon--sm layout-plan__shape-has-children';
      arrow.setAttribute('aria-hidden', 'true');
      btn.appendChild(arrow);
    }

    // Events
    if (polygon.isActive) {
      btn.addEventListener('click', () => state.handleShapeClick(polygon.id));
      btn.addEventListener('mouseenter', () => state.hover(polygon.id));
      btn.addEventListener('focus', () => state.hover(polygon.id));
      btn.addEventListener('mouseleave', () => state.hover(null));
      btn.addEventListener('blur', () => state.hover(null));
    }

    container.appendChild(btn);
  });
}

// ---------------------------------------------------------------------------
// Detail panel
// ---------------------------------------------------------------------------

export function renderPanel(container: HTMLElement, state: LayoutPlanStateManager): void {
  clearElement(container);

  const selected = state.selectedPolygon;
  if (!selected) return;

  const header = document.createElement('div');
  header.className = 'layout-plan__panel-header';

  const title = document.createElement('h4');
  title.id = 'layout-plan-panel-title';
  title.className = 'layout-plan__panel-title';
  title.textContent = selected.name;
  header.appendChild(title);

  if (selected.property) {
    const badge = document.createElement('span');
    const badgeClasses = ['layout-plan__shape-btn-status'];
    if (!selected.property.isSold) badgeClasses.push('layout-plan__shape-btn-status--available');
    badge.className = badgeClasses.join(' ');
    badge.textContent = selected.property.isSold ? Drupal.t('Sold') : Drupal.t('Available');
    header.appendChild(badge);
  }

  container.appendChild(header);

  if (selected.property) {
    const prop = selected.property;
    const dl = document.createElement('dl');
    dl.className = 'layout-plan__panel-details';

    if (prop.surface) {
      dl.appendChild(createDt(Drupal.t('Surface')));
      dl.appendChild(createDd(`${prop.surface} ${Drupal.t('m²')}`));
    }
    if (prop.surfaceBalcony) {
      dl.appendChild(createDt(Drupal.t('Balcony')));
      dl.appendChild(createDd(`${prop.surfaceBalcony} ${Drupal.t('m²')}`));
    }
    if (prop.rooms) {
      dl.appendChild(createDt(Drupal.t('Rooms')));
      dl.appendChild(createDd(prop.rooms));
    }
    if (prop.floor) {
      dl.appendChild(createDt(Drupal.t('Floor')));
      dl.appendChild(createDd(prop.floor));
    }
    if (prop.peb) {
      dl.appendChild(createDt(Drupal.t('PEB')));
      dl.appendChild(createDd(prop.peb));
    }

    container.appendChild(dl);

    if (prop.url) {
      const link = document.createElement('a');
      link.href = prop.url;
      link.className = 'layout-plan__panel-link';
      link.textContent = Drupal.t('View property');
      container.appendChild(link);
    }
  }
}

function createDt(text: string): HTMLElement {
  const dt = document.createElement('dt');
  dt.textContent = text;
  return dt;
}

function createDd(text: string): HTMLElement {
  const dd = document.createElement('dd');
  dd.textContent = text;
  return dd;
}

// ---------------------------------------------------------------------------
// Plan switcher
// ---------------------------------------------------------------------------

/**
 * The visitor-facing name of a plan, falling back to its position.
 */
export function planLabel(layer: NormalizedRootLayer, index: number): string {
  return layer.label || Drupal.t('Plan @number', { '@number': index + 1 }, { context: 'Layout plan' });
}

/**
 * Renders one button per top-level plan.
 *
 * A project usually has a single plan, in which case there is nothing to switch
 * between and the nav stays hidden. When roots are drawn on different
 * background images they cannot share a canvas, so each becomes a plan the
 * visitor picks from here.
 *
 * The plans themselves never change, so the buttons are built once and later
 * renders only move the active state. Rebuilding them would destroy the button
 * the visitor just activated and drop keyboard focus to the document body.
 */
export function renderPlanSwitcher(container: HTMLElement, state: LayoutPlanStateManager): void {
  const layers = state.rootLayers;

  if (layers.length < 2) {
    clearElement(container);
    container.setAttribute('hidden', '');
    return;
  }

  container.removeAttribute('hidden');

  let buttons = Array.from(container.querySelectorAll<HTMLButtonElement>('.layout-plan__plan-btn'));

  if (buttons.length !== layers.length) {
    clearElement(container);

    const list = document.createElement('ul');
    list.className = 'layout-plan__plan-list';

    buttons = layers.map((layer, index) => {
      const li = document.createElement('li');

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'layout-plan__plan-btn';
      btn.textContent = planLabel(layer, index);
      btn.addEventListener('click', () => state.selectLayer(index));

      li.appendChild(btn);
      list.appendChild(li);

      return btn;
    });

    container.appendChild(list);
  }

  buttons.forEach((btn, index) => {
    const isActive = index === state.activeLayerIndex;
    btn.classList.toggle('layout-plan__plan-btn--active', isActive);
    btn.setAttribute('aria-current', isActive ? 'true' : 'false');
  });
}

// ---------------------------------------------------------------------------
// Breadcrumbs
// ---------------------------------------------------------------------------

/**
 * Renders a breadcrumb trail showing the drill-down path.
 * Always visible — at root shows just the project name as the current item.
 */
export function renderBreadcrumbs(container: HTMLElement, state: LayoutPlanStateManager): void {
  clearElement(container);
  container.removeAttribute('hidden');

  const crumbs = state.breadcrumbs;

  const ol = document.createElement('ol');
  ol.className = 'layout-plan__breadcrumb-list';

  crumbs.forEach((bc, idx) => {
    const isLast = idx === crumbs.length - 1;
    const li = document.createElement('li');

    if (isLast) {
      const span = document.createElement('span');
      span.className = 'layout-plan__breadcrumb-current';
      span.setAttribute('aria-current', 'location');
      span.textContent = bc.label;
      li.appendChild(span);
    } else {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'layout-plan__breadcrumb-btn';
      btn.textContent = bc.label;
      btn.addEventListener('click', () => state.navigateToDepth(idx));
      li.appendChild(btn);

      const sep = document.createElement('span');
      sep.className = 'layout-plan__breadcrumb-sep';
      sep.setAttribute('aria-hidden', 'true');
      sep.textContent = '/';
      li.appendChild(sep);
    }

    ol.appendChild(li);
  });

  container.appendChild(ol);
}

// ---------------------------------------------------------------------------
// Sidebar header (prompt + back button)
// ---------------------------------------------------------------------------

/**
 * Updates the sidebar prompt text and back button visibility.
 * The back button markup is server-rendered via tp:button SDC in Twig;
 * JS only toggles its container's hidden attribute and wires the click.
 */
export function renderSidebarHeader(
  promptEl: HTMLElement,
  backEl: HTMLElement,
  state: LayoutPlanStateManager,
  defaultPrompt: string
): void {
  // Update prompt text
  const levelLabel = state.currentLevelLabel;
  promptEl.textContent = levelLabel ?? defaultPrompt;

  // Toggle back button visibility — show when drilled down OR property selected.
  const hasPropertySelected = state.selectedPolygon?.property != null;
  if (state.canGoBack || hasPropertySelected) {
    backEl.removeAttribute('hidden');
  } else {
    backEl.setAttribute('hidden', '');
  }
}
